import { TableMap } from '@atlaskit/editor-tables/table-map';
import { getSelectionRect } from '@atlaskit/editor-tables/utils';
import { tableCellMinWidth } from '@atlaskit/editor-common/styles';
import { akEditorTableNumberColumnWidth } from '@atlaskit/editor-shared-styles';
import { getParentNodeWidth } from '@atlaskit/editor-common/node-width';
import { updateColumnWidths } from '../../transforms';
import { getSelectedColumnIndexes, updateResizeHandles } from '../../utils';
import { evenColumns, setDragging, stopResizing } from './commands';
import { getPluginState } from './plugin-factory';
import { currentColWidth, getLayoutSize, getResizeState, pointsAtCell, resizeColumn, updateControls } from './utils';
import { ACTION_SUBJECT, EVENT_TYPE, TABLE_ACTION } from '@atlaskit/editor-common/analytics';
import { getParentWidthWithoutPadding } from './utils/misc';
export var handleMouseDown = function handleMouseDown(view, event, localResizeHandlePos, getEditorContainerWidth, getEditorFeatureFlags, editorAnalyticsAPI) {
  var state = view.state,
    dispatch = view.dispatch;
  var editorDisabled = !view.editable;
  var domAtPos = view.domAtPos.bind(view);
  if (editorDisabled || localResizeHandlePos === null || !pointsAtCell(state.doc.resolve(localResizeHandlePos))) {
    return false;
  }
  event.preventDefault();
  var mouseDownTime = event.timeStamp;
  var cell = state.doc.nodeAt(localResizeHandlePos);
  var $cell = state.doc.resolve(localResizeHandlePos);
  var originalTable = $cell.node(-1);
  var start = $cell.start(-1);
  var dom = domAtPos(start).node;
  if (dom && dom.nodeName !== 'TABLE') {
    dom = dom.closest('table');
  }
  var containerWidth = getEditorContainerWidth();
  var parentWidth = getParentNodeWidth(start, state, containerWidth);

  // TODO - refactor this logic into getParentNodeWidth() in editor-common [ED-16718]
  var parentActualWidth = getParentWidthWithoutPadding(parentWidth, start, state);
  var maxSize = parentActualWidth || getLayoutSize(dom.getAttribute('data-layout'), containerWidth.width, {});
  if (originalTable.attrs.isNumberColumnEnabled) {
    maxSize -= akEditorTableNumberColumnWidth;
  }
  var resizeState = getResizeState({
    minWidth: tableCellMinWidth,
    maxSize: maxSize,
    table: originalTable,
    tableRef: dom,
    start: start,
    domAtPos: domAtPos
  });
  if (evenColumns({
    resizeState: resizeState,
    table: originalTable,
    start: start,
    event: event
  })(state, dispatch)) {
    finish(event);
    return true;
  }
  var width = currentColWidth(view, localResizeHandlePos, cell.attrs);
  setDragging({
    startX: event.clientX,
    startWidth: width
  })(state, dispatch);
  function finish(event) {
    window.removeEventListener('mouseup', finish);
    window.removeEventListener('mousemove', move);
    var clientX = event.clientX;
    var state = view.state,
      dispatch = view.dispatch;
    var _getPluginState = getPluginState(state),
      dragging = _getPluginState.dragging,
      resizeHandlePos = _getPluginState.resizeHandlePos;
    if (resizeHandlePos === null) {
      return stopResizing()(state, dispatch);
    }
    if (!pointsAtCell(state.doc.resolve(resizeHandlePos))) {
      return;
    }
    // resizeHandlePos could be remapped via a collab change.
    // Fetch a fresh reference of the table.
    var $cell = state.doc.resolve(resizeHandlePos);
    var start = $cell.start(-1);
    var table = $cell.node(-1);

    // If we let go in the same place we started, dont need to do anything.
    if (dragging && clientX === dragging.startX) {
      return stopResizing()(state, dispatch);
    }
    var tr = state.tr;
    if (dragging) {
      var startX = dragging.startX;

      // If the table has changed (via collab for example) don't apply column widths
      // For example, if a table col is deleted we won't be able to reliably remap the new widths
      // There may be a more elegant solution to this, to avoid a jarring experience.
      if (table.eq(originalTable)) {
        var map = TableMap.get(table);
        var colIndex = map.colCount($cell.pos - start) + ($cell.nodeAfter ? $cell.nodeAfter.attrs.colspan : 1) - 1;
        var selectionRect = getSelectionRect(state.selection);
        var selectedColumns = selectionRect ? getSelectedColumnIndexes(selectionRect) : [];
        // only selected (or selected - 1) columns should be distributed
        var resizingSelectedColumns = selectedColumns.indexOf(colIndex) > -1 || selectedColumns.indexOf(colIndex + 1) > -1;
        var newResizeState = resizeColumn(resizeState, colIndex, clientX - startX, dom, resizingSelectedColumns ? selectedColumns : undefined);
        tr = updateColumnWidths(newResizeState, table, start)(tr);
        if (colIndex === map.width - 1) {
          var mouseUpTime = event.timeStamp;
          editorAnalyticsAPI === null || editorAnalyticsAPI === void 0 ? void 0 : editorAnalyticsAPI.attachAnalyticsEvent({
            action: TABLE_ACTION.ATTEMPTED_TABLE_WIDTH_CHANGE,
            actionSubject: ACTION_SUBJECT.TABLE,
            actionSubjectId: null,
            attributes: {
              type: 'table-border',
              position: 'right',
              duration: mouseUpTime - mouseDownTime,
              delta: Math.abs(clientX - dragging.startX)
            },
            eventType: EVENT_TYPE.UI
          })(tr);
        }
      }
      return stopResizing(tr)(state, dispatch);
    }
  }
  function move(event) {
    var clientX = event.clientX,
      which = event.which;
    var state = view.state;
    var _getPluginState2 = getPluginState(state),
      dragging = _getPluginState2.dragging,
      resizeHandlePos = _getPluginState2.resizeHandlePos;
    if (!which || !dragging || resizeHandlePos === null || !pointsAtCell(state.doc.resolve(resizeHandlePos))) {
      return finish(event);
    }
    var $cell = state.doc.resolve(resizeHandlePos);
    var table = $cell.node(-1);
    var map = TableMap.get(table);
    var colIndex = map.colCount($cell.pos - $cell.start(-1)) + $cell.nodeAfter.attrs.colspan - 1;
    resizeColumn(resizeState, colIndex, clientX - dragging.startX, dom);
    updateControls(getEditorFeatureFlags)(state);
    updateResizeHandles(dom);
  }
  window.addEventListener('mouseup', finish);
  window.addEventListener('mousemove', move);
  return true;
};